import { useEffect, useMemo, useState } from "react";

import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Box,
    Typography,
    CircularProgress,
    Paper,
} from "@mui/material";

import TablePagination from "../../core/components/TablePagination";

const DataTable = ({
    columns = [],
    rows = [],
    loading = false,
    onRowClick,
    emptyText = "No data available",
    pagination = true,
    totalCount,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    maxHeight = { xs: "62vh", md: "min(70vh, 760px)" },
    embedded = false,
    mobileCardTitle,
    mobileCardSubtitle,
    mobileCardStatus,
    mobileCardActions,
    mobileCardFields,
}) => {
    const controlledPagination =
        typeof page === "number" &&
        typeof rowsPerPage === "number" &&
        typeof onPageChange === "function" &&
        typeof onRowsPerPageChange === "function";
    const [localPage, setLocalPage] = useState(0);
    const [localRowsPerPage, setLocalRowsPerPage] = useState(25);

    const activePage = controlledPagination ? page : localPage;
    const activeRowsPerPage = controlledPagination ? rowsPerPage : localRowsPerPage;
    const activeTotal = Number(totalCount ?? rows.length);
    const showPagination = !loading && pagination && rows.length > 0 && activeTotal > 10;
    const useMobileCards = typeof mobileCardTitle === "function";
    const showMobileScrollHint = !useMobileCards && columns.length > 3 && rows.length > 0;

    const visibleRows = useMemo(() => {
        if (!pagination || controlledPagination) {
            return rows;
        }

        const start = localPage * localRowsPerPage;
        return rows.slice(start, start + localRowsPerPage);
    }, [controlledPagination, localPage, localRowsPerPage, pagination, rows]);

    useEffect(() => {
        if (controlledPagination || !pagination) return;
        setLocalPage(0);
    }, [controlledPagination, pagination, rows.length]);

    const handlePageChange = (nextPage) => {
        if (controlledPagination) {
            onPageChange(nextPage);
            return;
        }

        setLocalPage(nextPage);
    };

    const handleRowsPerPageChange = (nextRowsPerPage) => {
        if (controlledPagination) {
            onRowsPerPageChange(nextRowsPerPage);
            return;
        }

        setLocalRowsPerPage(nextRowsPerPage);
        setLocalPage(0);
    };

    return (
        <Paper
            elevation={0}
            sx={{
                position: "relative",
                borderRadius: 3,
                border: "1px solid",
                borderColor: "rgba(148,163,184,0.18)",
                overflow: "hidden",
                backgroundColor: "transparent",
                width: "100%",
                minWidth: 0,
                ...(embedded && {
                    border: "none",
                    borderRadius: 0,
                }),
            }}
        >
            <TableContainer
                sx={{
                    display: useMobileCards ? { xs: "none", sm: "block" } : "block",
                    overflow: "auto",
                    maxHeight,
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Table stickyHeader size="small" sx={{ minWidth: { xs: 680, md: 720 } }}>
                    {/* HEADER */}
                    <TableHead>
                        <TableRow
                            sx={{
                                "& th": {
                                    backgroundColor: "rgba(248,250,252,0.98)",
                                },
                            }}
                        >
                            {columns.map((col) => (
                                <TableCell
                                    key={col.field}
                                    align={col.align || "left"}
                                    sx={{
                                        fontWeight: 700,
                                        color: "text.secondary",
                                        textTransform: "uppercase",
                                        letterSpacing: 0.6,
                                        fontSize: "0.76rem",
                                        whiteSpace: "nowrap",
                                        width: col.width || "auto",
                                        borderBottomColor: "rgba(148,163,184,0.18)",
                                        py: 1.25,
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 2,
                                    }}
                                >
                                    {col.headerName}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                        {/* LOADING */}
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        py={6}
                                        px={2}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            /* EMPTY */
                            <TableRow>
                                <TableCell colSpan={columns.length}>
                                    <Box
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        py={6}
                                        px={2}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ textAlign: "center" }}
                                        >
                                            {emptyText}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            /* DATA */
                            visibleRows.map((row, index) => (
                                <TableRow
                                    key={row._id || index}
                                    hover={Boolean(onRowClick)}
                                    onClick={() => onRowClick && onRowClick(row)}
                                    sx={{
                                        cursor: onRowClick ? "pointer" : "default",
                                        transition: "background-color 0.15s ease",
                                        "& td": {
                                            borderBottomColor: "rgba(148,163,184,0.16)",
                                        },
                                        "&:hover": {
                                            backgroundColor: onRowClick
                                                ? "rgba(59,130,246,0.04)"
                                                : "transparent",
                                        },
                                        "&:last-child td": { borderBottom: 0 },
                                    }}
                                >
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.field}
                                            align={col.align || "left"}
                                            sx={{
                                                verticalAlign: "middle",
                                                py: { xs: 1.15, md: 1.25 },
                                                px: { xs: 1.25, md: 2 },
                                                fontSize: { xs: "0.86rem", md: "0.9rem" },
                                                whiteSpace: col.wrap ? "normal" : "nowrap",
                                            }}
                                        >
                                            {col.render
                                                ? col.render(row)
                                                : row[col.field] ?? "-"}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {useMobileCards && (
                <Box sx={{ display: { xs: "block", sm: "none" } }}>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={6} px={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : rows.length === 0 ? (
                        <Box display="flex" justifyContent="center" alignItems="center" py={6} px={2}>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center" }}>
                                {emptyText}
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {visibleRows.map((row, index) => {
                                const fields = typeof mobileCardFields === "function" ? mobileCardFields(row) : [];
                                const actions = typeof mobileCardActions === "function" ? mobileCardActions(row) : null;
                                const status = typeof mobileCardStatus === "function" ? mobileCardStatus(row) : null;

                                return (
                                    <Box
                                        key={row._id || index}
                                        sx={{
                                            p: 1.5,
                                            borderTop: index === 0 ? "none" : "1px solid",
                                            borderColor: "rgba(148,163,184,0.16)",
                                            bgcolor: "background.paper",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", justifyContent: "space-between" }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.25, wordBreak: "break-word" }}>
                                                    {mobileCardTitle(row)}
                                                </Typography>
                                                {mobileCardSubtitle && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                                                        {mobileCardSubtitle(row)}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {status && <Box sx={{ flexShrink: 0 }}>{status}</Box>}
                                        </Box>

                                        {fields.length > 0 && (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                                    gap: 1,
                                                    mt: 1.25,
                                                }}
                                            >
                                                {fields.filter(Boolean).map((field, fieldIndex) => (
                                                    <Box key={`${row._id || index}-${field.label || fieldIndex}`} sx={{ minWidth: 0 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, textTransform: "uppercase" }}>
                                                            {field.label}
                                                        </Typography>
                                                        <Typography component="div" variant="body2" sx={{ mt: 0.25, wordBreak: "break-word" }}>
                                                            {field.value ?? "-"}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}

                                        {actions && (
                                            <Box sx={{ mt: 1.25 }}>
                                                {actions}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            )}

            {showMobileScrollHint && (
                <Box
                    aria-hidden="true"
                    sx={{
                        display: { xs: "block", md: "none" },
                        position: "absolute",
                        top: 0,
                        right: 0,
                        bottom: showPagination ? 48 : 0,
                        width: 28,
                        pointerEvents: "none",
                        background:
                            "linear-gradient(90deg, rgba(248,250,252,0), rgba(248,250,252,0.92))",
                    }}
                />
            )}

            {showMobileScrollHint && (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        display: { xs: "block", md: "none" },
                        px: 1.5,
                        py: 0.75,
                        borderTop: "1px solid",
                        borderColor: "rgba(148,163,184,0.14)",
                        bgcolor: "rgba(248,250,252,0.72)",
                        fontWeight: 600,
                    }}
                >
                    Swipe horizontally to view more columns.
                </Typography>
            )}

            {!loading && pagination && rows.length > 0 && (
                <TablePagination
                    totalCount={activeTotal}
                    page={activePage}
                    rowsPerPage={activeRowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                />
            )}
        </Paper>
    );
};

export default DataTable;
